import { fuzzy, Searcher } from 'fast-fuzzy';
import memo from 'nano-memoize';

/**
 * Compare strings; returning a scalar between 0 and 1 where 1 means it's the same string and 0 means they're nothing alike.
 * Memoized due to it being a potentially expensive call.
 */
export const stringSimilarity = memo((string1: string, string2: string) =>
  fuzzy(string1, string2)
);

/**
 * Use this when you have a haystack (list of things to search) that doesn't change much, for optimisations.
 */
export const FuzzySearcher = Searcher;

const TOKEN_SPLIT = /[\s,/()-]+/;
const FUZZY_TOKEN_THRESHOLD = 0.7;

/**
 * True when every word in `query` approximately matches some word in `target`, regardless of
 * order. A query word matches a target word if it's a prefix of it, the target word is a prefix
 * of it (handles trailing punctuation like "v9.0" vs "v9"), or they're fuzzy-similar above
 * threshold (handles minor typos/abbreviation drift).
 */
export const matchesAllTokens = (query: string, target: string): boolean => {
  const queryTokens = query.trim().toLowerCase().split(TOKEN_SPLIT).filter(Boolean);
  if (!queryTokens.length) return true;

  const targetTokens = target.toLowerCase().split(TOKEN_SPLIT).filter(Boolean);

  return queryTokens.every((qToken) =>
    targetTokens.some(
      (tToken) =>
        tToken.startsWith(qToken) ||
        qToken.startsWith(tToken) ||
        fuzzy(qToken, tToken) >= FUZZY_TOKEN_THRESHOLD
    )
  );
};
